import type {PDFViewer, EventBus} from "pdfjs-dist/legacy/web/pdf_viewer.mjs"
import {ScaleDefaultInfo} from "./constants";
export interface ViewerApi {
    find: (text: string) => void;
    findAgain: (text: string, forward: boolean) => void;
    zoomDown: () => void;
    zoomUp: () => void;
    fitWidth: () => void;
    goToPage: (pageNumber: number) => void;
}

export const createApi = (eventBus: EventBus, pdfViewer: PDFViewer): ViewerApi => {

    // pdf.js does not re-anchor scroll when `currentScaleValue` changes in
    // this embedding — it makes zero scrollTop writes — so a zoom leaves the
    // scroll position frozen while every page grows/shrinks, landing the
    // viewport on a different page (issue #764). Re-anchor manually: remember
    // the page at the top of the viewport and the offset into it, change the
    // scale, then put that same page+offset back. Page layout updates
    // synchronously when the scale changes, so this runs inline.
    const preserveScrollAnchor = (changeScale: () => void) => {
        const container = pdfViewer.container
        const pages = Array.from(container.querySelectorAll<HTMLElement>(".page"))
        if (!pages.length) {
            changeScale()
            return
        }
        const topOf = (page: HTMLElement) => page.offsetTop - container.offsetTop
        const scrollTop = container.scrollTop
        const anchor = pages.find((page) => topOf(page) + page.offsetHeight > scrollTop + 1) ?? pages[0]
        const offsetIntoAnchor = scrollTop - topOf(anchor)
        const heightBefore = anchor.offsetHeight

        changeScale()

        const heightAfter = anchor.offsetHeight
        if (heightBefore > 0) {
            container.scrollTop = topOf(anchor) + offsetIntoAnchor * (heightAfter / heightBefore)
        }
    }

    // After a find, pdf.js scrolls the selected match into view via its text
    // layer — but only if the match's page is already rendered. When the match
    // is on a page outside pdf.js's render buffer (common once zoomed, since
    // taller pages shrink the buffer) it updates currentPageNumber but never
    // moves the viewport, so the page never renders and the match never appears
    // — navigation then looks dead (issue #764). If no selected highlight
    // materialised, bring the match's page into view ourselves so pdf.js renders
    // it and finishes scrolling to the match.
    const ensureSelectedMatchVisible = () => {
        const container = pdfViewer.container
        if (container.querySelector(".highlight.selected")) {
            return
        }
        const pageEl = container.querySelector<HTMLElement>(`.page[data-page-number="${pdfViewer.currentPageNumber}"]`)
        if (pageEl) {
            container.scrollTop = pageEl.offsetTop - container.offsetTop
        }
    }

    return {
            find: (text: string) => {
                eventBus.dispatch("find", {
                    type: "",
                    source: window,
                    caseSensitive: false,
                    findPrevious: false,
                    highlightAll: true,
                    entireWord: false,
                    matchDiacritics: true,
                    query: text
                })
                setTimeout(ensureSelectedMatchVisible, 100)
            },
            findAgain: (text: string, forward: boolean) => {
                eventBus.dispatch("find", {
                    type: "again",
                    source: window,
                    caseSensitive: false,
                    findPrevious: !forward,
                    highlightAll: true,
                    entireWord: false,
                    matchDiacritics: true,
                    query: text
                })
                setTimeout(ensureSelectedMatchVisible, 100)
            },
            zoomDown: () => preserveScrollAnchor(() => {
                const delta = Number((pdfViewer.currentScale / ScaleDefaultInfo.defaultScaleDelta).toFixed(2))
                const newScale = Math.ceil(delta * 10) / 10
                pdfViewer.currentScaleValue = Math.max(ScaleDefaultInfo.minScale, newScale).toFixed(2)
            }),
            zoomUp: () => preserveScrollAnchor(() => {
                const delta = Number((pdfViewer.currentScale * ScaleDefaultInfo.defaultScaleDelta).toFixed(2))
                const newScale = Math.floor(delta * 10) / 10
                pdfViewer.currentScaleValue = Math.min(ScaleDefaultInfo.maxScale, newScale).toFixed(2)
            }),
            fitWidth: () => preserveScrollAnchor(() => {
                pdfViewer.currentScaleValue = ScaleDefaultInfo.defaultScale
            }),
            goToPage: (pageNumber: number) => {
                pdfViewer.currentPageNumber = Number(pageNumber)
            }
        }
}